/**
 * This component will show the booking info and calculated total price.
 * I.e. dates and other details related to payment decision in receipt format.
 */
import React from 'react';
import { number, oneOf, string } from 'prop-types';
import classNames from 'classnames';

import { FormattedMessage, intlShape, injectIntl } from '../../util/reactIntl';
import {
  propTypes,
  LISTING_UNIT_TYPES,
  LINE_ITEM_CUSTOMER_COMMISSION,
  LINE_ITEM_PROVIDER_COMMISSION,
  DEAL_LISTING_TYPE,
  LINE_ITEM_HOUR,
  DATE_TYPE_DATETIME,
  STAFF_LISTING_TYPE,
} from '../../util/types';

import LineItemBookingPeriod from './LineItemBookingPeriod';
import LineItemBasePriceMaybe from './LineItemBasePriceMaybe';
import LineItemSubTotalMaybe from './LineItemSubTotalMaybe';
import LineItemShippingFeeMaybe from './LineItemShippingFeeMaybe';
import LineItemPickupFeeMaybe from './LineItemPickupFeeMaybe';
import LineItemCustomerCommissionMaybe from './LineItemCustomerCommissionMaybe';
import LineItemCustomerCommissionRefundMaybe from './LineItemCustomerCommissionRefundMaybe';
import LineItemProviderCommissionMaybe from './LineItemProviderCommissionMaybe';
import LineItemProviderCommissionRefundMaybe from './LineItemProviderCommissionRefundMaybe';
import LineItemVatTaxOnNowFeeMaybe from './LineItemVatTaxOnNowFeeMaybe';
import LineItemDiscountCodeMaybe from './LineItemDiscountCodeMaybe';
import LineItemTapFeeMaybe from './LineItemTapFeeMaybe';
import LineItemRefundMaybe from './LineItemRefundMaybe';
import LineItemTotalPrice from './LineItemTotalPrice';
import LineItemUnknownItemsMaybe from './LineItemUnknownItemsMaybe';

import css from './OrderBreakdown.module.css';

export const OrderBreakdownComponent = props => {
  const {
    rootClassName,
    className,
    userRole,
    transaction,
    booking,
    intl,
    dateType,
    timeZone,
    currency,
    marketplaceName,
    showLineItems = true,
    dealChildTransactions = [],
    bookedService,
    negotiatedDurationHours,
  } = props;

  const { listingType } = transaction?.listing?.attributes?.publicData || {};
  const isDeal = listingType === DEAL_LISTING_TYPE;
  const isStaff = listingType === STAFF_LISTING_TYPE;
  const { title: listingTitle } = transaction?.listing?.attributes || {};
  // Snapshotted at booking time (server/api/initiate-privileged.js) from the
  // linked service's category — a fixed-model booking's base line item
  // always has quantity 1 (its price is the session's flat total, not a
  // per-hour rate), so it needs a duration-free label.
  const isFixedModel = transaction?.attributes?.protectedData?.transactionModel === 'fixed';
  // A negotiated quote's base line item is the agreed Labour/Service amount
  // only (materials/travel/inspection ride as their own pass-through line
  // items below) -- quantity is always 1, so the "x Nh" framing is as wrong
  // here as it is for a fixed-model booking, and the amount isn't "the
  // service price" in the fixed-model sense either, so it gets its own label.
  const isNegotiatedModel = transaction?.attributes?.protectedData?.transactionModel === 'negotiated';
  // Point-in-time copy of the listing's declared materials, saved at booking
  // time (see server/api/initiate-privileged.js) -- lets LineItemUnknownItemsMaybe
  // label each `line-item/material-item-N` by its real name instead of a
  // generic humanized code.
  const materialsSnapshot = transaction?.attributes?.protectedData?.materialsSnapshot;
  const discountCode = transaction?.attributes?.protectedData?.discountCode?.code || null;

  const isCustomer = userRole === 'customer';
  const isProvider = userRole === 'provider';
  const allLineItems = transaction.attributes.lineItems || [];

  // We'll show only line-items that are specific for the current userRole (customer vs provider)
  const lineItems = allLineItems.filter(lineItem => lineItem.includeFor.includes(userRole));
  const unitLineItems = lineItems.filter(
    item => LISTING_UNIT_TYPES.includes(item.code) && !item.reversal
  );
  // Line-item code that matches with base unit: day, night, hour, item
  const lineItemUnitType = unitLineItems?.[0]?.code;

  const hasCommissionLineItem = lineItems.find(item => {
    const hasCustomerCommission = isCustomer && item.code === LINE_ITEM_CUSTOMER_COMMISSION;
    const hasProviderCommission = isProvider && item.code === LINE_ITEM_PROVIDER_COMMISSION;
    return (hasCustomerCommission || hasProviderCommission) && !item.reversal;
  });

  const classes = classNames(rootClassName || css.root, className);

  /**
   * OrderBreakdown contains different line items:
   *
   * LineItemBookingPeriod: prints booking start and booking end types. Prop dateType
   * determines if the date and time or only the date is shown
   *
   * LineItemShippingFeeMaybe: prints the shipping fee (combining additional fee of
   * additional items into it).
   *
   * LineItemShippingFeeRefundMaybe: prints the amount of refunded shipping fee
   *
   * LineItemBasePriceMaybe: prints the base price calculation for the listing, e.g.
   * "$150.00 * 2 nights $300"
   *
   * LineItemUnknownItemsMaybe: prints the line items that are unknown. In ideal case there
   * should not be unknown line items. If you are using custom pricing, you should create
   * new custom line items if you need them.
   *
   * LineItemSubTotalMaybe: prints subtotal of line items before possible
   * commission or refunds
   *
   * LineItemRefundMaybe: prints the amount of refund
   *
   * LineItemCustomerCommissionMaybe: prints the amount of customer commission
   * The default transaction process used by this template doesn't include the customer commission.
   *
   * LineItemCustomerCommissionRefundMaybe: prints the amount of refunded customer commission
   *
   * LineItemProviderCommissionMaybe: prints the amount of provider commission
   *
   * LineItemProviderCommissionRefundMaybe: prints the amount of refunded provider commission
   *
   * LineItemTotalPrice: prints total price of the transaction
   *
   */

  // const unitLineItems = lineItems.filter(li => li.code === lineItemUnitType);
  const basePrices = unitLineItems.map((uli, idx) => (
    <LineItemBasePriceMaybe
      lineItems={[uli]}
      key={idx}
      code={uli?.code}
      intl={intl}
      isFixedModel={isFixedModel}
      isNegotiatedModel={isNegotiatedModel}
    />
  ));

  const dealChildTxBookings = dealChildTransactions?.map(childTx => childTx?.booking)?.filter(booking => booking !== null);
  const { title: serviceTitle } = bookedService?.attributes || {};
  const negotiatedStaffName = transaction?.attributes?.protectedData?.selectedStaffName || null;

  return (
    <div className={classes}>
      {showLineItems ? (
        <>
          {/* Once a real booking exists, its start/end (below) already implies
              the duration, so this only fills the gap between offer-acceptance
              and the customer actually completing checkout. */}
          {isNegotiatedModel && !booking && negotiatedDurationHours ? (
            <div className={css.lineItem}>
              <span className={css.itemLabel}>
                <FormattedMessage id="OrderBreakdown.serviceDurationLabel" />
              </span>
              <span className={css.itemValue}>
                {intl.formatMessage(
                  { id: 'OrderBreakdown.serviceDurationValue' },
                  { hours: negotiatedDurationHours }
                )}
              </span>
            </div>
          ) : null}

          {
            isDeal ? dealChildTxBookings?.map((booking, idx) => (
              <LineItemBookingPeriod
                booking={booking}
                code={LINE_ITEM_HOUR}
                dateType={DATE_TYPE_DATETIME}
                timeZone={timeZone}
              />
            )) : <LineItemBookingPeriod
              booking={booking}
              code={lineItemUnitType}
              dateType={dateType}
              timeZone={timeZone}
            />
          }

          {Array.isArray(dealChildTransactions) ?
            dealChildTransactions.map(childTx => {
              const listing = childTx?.listing || {};
              const { title, publicData } = listing?.attributes || {};
              const { listingType } = publicData || {}
              return (
                <div className={css.lineItem}>
                  <span className={css.itemLabel}>
                    <FormattedMessage id={`OrderBreakdown.${listingType}`} />
                  </span>
                  <span className={css.itemValue}>{title}</span>
                </div>
              )
            })
            : null}

          {/* Negotiated-flow bookings keep the SERVICE listing as transaction.listing
              (the staff reservation lives on a separate, linked transaction), so the
              staff name is denormalized onto protectedData at booking time instead. */}
          {!isStaff && negotiatedStaffName ?
            <div className={css.lineItem}>
              <span className={css.itemLabel}>
                <FormattedMessage id={`OrderBreakdown.staff`} />
              </span>
              <span className={css.itemValue}>{negotiatedStaffName}</span>
            </div>
            : null}

          {isStaff && serviceTitle ?
            <>
              <div className={css.lineItem}>
                <span className={css.itemLabel}>
                  <FormattedMessage id={`OrderBreakdown.staff`} />
                </span>
                <span className={css.itemValue}>{listingTitle}</span>
              </div>
              <div className={css.lineItem}>
                <span className={css.itemLabel}>
                  <FormattedMessage id={`OrderBreakdown.service`} />
                </span>
                <span className={css.itemValue}>{serviceTitle}</span>
              </div>
            </>
            : null}

          {basePrices}
          <LineItemShippingFeeMaybe lineItems={lineItems} intl={intl} />
          <LineItemPickupFeeMaybe lineItems={lineItems} intl={intl} />
          <LineItemUnknownItemsMaybe
            lineItems={lineItems}
            isProvider={isProvider}
            intl={intl}
            materialsSnapshot={materialsSnapshot}
          />

          <LineItemSubTotalMaybe
            lineItems={lineItems}
            code={lineItemUnitType}
            userRole={userRole}
            intl={intl}
            marketplaceCurrency={currency}
          />
          <LineItemRefundMaybe lineItems={lineItems} intl={intl} marketplaceCurrency={currency} />

          <LineItemCustomerCommissionMaybe
            lineItems={lineItems}
            isCustomer={isCustomer}
            marketplaceName={marketplaceName}
            intl={intl}
          />
          <LineItemCustomerCommissionRefundMaybe
            lineItems={lineItems}
            isCustomer={isCustomer}
            marketplaceName={marketplaceName}
            intl={intl}
          />

          <LineItemProviderCommissionMaybe
            lineItems={lineItems}
            isProvider={isProvider}
            marketplaceName={marketplaceName}
            intl={intl}
          />
          <LineItemVatTaxOnNowFeeMaybe
            lineItems={lineItems}
            isProvider={isProvider}
            intl={intl}
          />
          <LineItemTapFeeMaybe lineItems={lineItems} isProvider={isProvider} intl={intl} />
          <LineItemProviderCommissionRefundMaybe
            lineItems={lineItems}
            isProvider={isProvider}
            marketplaceName={marketplaceName}
            intl={intl}
          />
          <LineItemDiscountCodeMaybe
            lineItems={lineItems}
            isProvider={isProvider}
            discountCode={discountCode}
            intl={intl}
          />
        </>
      ) : null}
      <LineItemTotalPrice transaction={transaction} isProvider={isProvider} intl={intl} />

      {hasCommissionLineItem ? (
        <span className={css.feeInfo}>
          <FormattedMessage id="OrderBreakdown.commissionFeeNote" />
        </span>
      ) : null}
    </div>
  );
};

OrderBreakdownComponent.defaultProps = {
  rootClassName: null,
  className: null,
  booking: null,
  dateType: null,
};

OrderBreakdownComponent.propTypes = {
  rootClassName: string,
  className: string,

  marketplaceName: string.isRequired,
  userRole: oneOf(['customer', 'provider']).isRequired,
  transaction: propTypes.transaction.isRequired,
  booking: propTypes.booking,
  dateType: propTypes.dateType,
  negotiatedDurationHours: number,

  // from injectIntl
  intl: intlShape.isRequired,
};

const OrderBreakdown = injectIntl(OrderBreakdownComponent);

OrderBreakdown.displayName = 'OrderBreakdown';

export default OrderBreakdown;
